import { warn } from './logger';
/**
 * UTILIDADES DE PERMISOS
 * 
 * Funciones helper para verificar permisos de usuarios.
 * Compatible con el nuevo sistema many-to-many del backend.
 */

/**
 * Verificar si el usuario tiene un permiso específico por ID
 * A partir de ahora usamos EXCLUSIVAMENTE IDs para áreas sensibles
 * @param {Object} user - Objeto de usuario del AuthContext
 * @param {number} permissionId - ID del permiso requerido
 * @returns {boolean} true si tiene el permiso
 */
export const hasPermission = (user, permissionId) => {
  if (!user) return false;

  // Si es Admin, tiene todos los permisos
  const isAdmin = user.role === 'Admin' || user.rol === 'Admin' || 
                  user.role === 'admin' || user.rol === 'admin';
  if (isAdmin) {
    return true;
  }

  // Validar que permissionId sea un número
  const permId = typeof permissionId === 'number' ? permissionId : parseInt(permissionId, 10);
  if (isNaN(permId)) {
    warn('[hasPermission] ID de permiso inválido:', permissionId);
    return false;
  }

  // Obtener IDs de permisos del usuario
  const permissionIds = user.permissionIds || [];

  // Verificar por ID (EXCLUSIVAMENTE por ID)
  return permissionIds.includes(permId);
};

/**
 * Verificar si el usuario tiene alguno de los permisos especificados por ID
 * A partir de ahora usamos EXCLUSIVAMENTE IDs para áreas sensibles
 * @param {Object} user - Objeto de usuario del AuthContext
 * @param {Array<number>} permissionIds - Array de IDs de permisos
 * @returns {boolean} true si tiene al menos uno de los permisos
 */
export const hasAnyPermission = (user, permissionIds) => {
  if (!user || !Array.isArray(permissionIds) || permissionIds.length === 0) {
    return false;
  }

  // Normalizar IDs
  const permIds = permissionIds.map(id => typeof id === 'number' ? id : parseInt(id, 10)).filter(id => !isNaN(id));
  if (permIds.length === 0) {
    return false;
  }

  return permIds.some(permissionId => hasPermission(user, permissionId));
};

/**
 * Verificar si el usuario tiene todos los permisos especificados por ID
 * A partir de ahora usamos EXCLUSIVAMENTE IDs para áreas sensibles
 * @param {Object} user - Objeto de usuario del AuthContext
 * @param {Array<number>} permissionIds - Array de IDs de permisos
 * @returns {boolean} true si tiene todos los permisos
 */
export const hasAllPermissions = (user, permissionIds) => {
  if (!user || !Array.isArray(permissionIds) || permissionIds.length === 0) {
    return false;
  }

  // Normalizar IDs
  const permIds = permissionIds.map(id => typeof id === 'number' ? id : parseInt(id, 10)).filter(id => !isNaN(id));
  if (permIds.length === 0) {
    return false;
  }

  return permIds.every(permissionId => hasPermission(user, permissionId));
};

/**
 * IDs de permisos comunes (según init-roles-permissions.js)
 */
export const PERMISSION_IDS = {
  // Panel Admin
  PANEL_ADMIN: 741258,
  
  // Navbar
  VER_BOTON_PANEL: 526318,
  
  // Dashboard
  VER_PANEL_DASHBOARD: 986321,
  
  // Cursos
  VER_PANEL_CURSOS: 723458,
  VER_CURSOS: 458965,
  VER_CONTENIDO_CURSOS: 877885,
  EDITAR_CONTENIDO_CURSOS: 125414,
  EDITAR_EXAMENES_CURSOS: 254784,
  ACTIVAR_CURSOS: 784512,
  ARCHIVAR_CURSO: 142598,
  
  // Usuarios
  PANEL_USERS: 963741,
  VER_USUARIOS: 325147,
  EDITAR_USUARIO: 698741,
  ELIMINAR_USUARIO: 147852,
  
  // Roles
  VER_ROLES: 123456,
  CREAR_ROLES: 234567,
  EDITAR_ROLES: 681357,
  ELIMINAR_ROLES: 792468,
  
  // Estadísticas (panel)
  VER_ESTADISTICAS: 963258,
  
  // Eventos (PanelEventos id único; no reutilizar VerPedidos)
  PANEL_EVENTOS: 852700,
  GESTIONAR_EVENTOS: 369741,
  VER_EVENTOS: 147258,
  VER_EVENTOS_ARCHIVADOS: 852963,
  CREAR_EVENTOS: 741147,
  EDITAR_EVENTOS: 963852,
  ACTIVAR_EVENTOS: 258369,
  ARCHIVAR_EVENTOS: 147741,
  ELIMINAR_EVENTOS: 852258,
  
  // Productos
  PANEL_PRODUCTOS: 258852,
  GESTIONAR_TIENDA: 369147,
  VER_PRODUCTOS: 159753,
  CREAR_PRODUCTOS: 357159,
  EDITAR_PRODUCTOS: 753951,
  ELIMINAR_PRODUCTOS: 951357,
  ACTIVAR_PRODUCTOS: 159357,
  
  // Bundles
  VER_BUNDLES: 456789,
  CREAR_BUNDLES: 789123,
  EDITAR_BUNDLES: 321654,
  ELIMINAR_BUNDLES: 654987,
  GESTIONAR_BUNDLES: 987321,
  
  // Items
  PANEL_ITEMS: 456258,
  CREAR_ITEMS: 369963,
  VER_TODOS_ITEMS: 456123,
  VER_ITEMS: 789456,
  EDITAR_ITEMS: 123789,
  ACTIVAR_ITEMS: 654321,
  ARCHIVAR_ITEMS: 468357,
  VER_ITEMS_ARCHIVADOS: 987654,
  
  // Categorías
  CREAR_CATEGORIA: 258963,
  EDITAR_CATEGORIA: 468246,
  ARCHIVAR_CATEGORIA: 357135,
  
  // Newsletter
  PANEL_NEWSLETTER: 789147,
  GESTIONAR_NEWSLETTER: 852147,
  CREAR_NEWSLETTER: 369258,
  EDITAR_NEWSLETTER: 357924,
  ARCHIVAR_NEWSLETTER: 147963,
  PUBLICAR_NEWSLETTER: 741852,
  VER_NEWSLETTER_ADMIN: 963147,
  VER_TODOS_NEWSLETTER_ADMIN: 852369,
  VER_NEWSLETTERS_ARCHIVADOS: 345678,
  ESTADISTICAS_NEWSLETTER: 135792,
  
  // Mailing
  PANEL_MAILING: 741963,
  ENVIAR_MAILS: 246813,
  VER_MAILS: 468135,
  VER_DATOS_USUARIOS_MAILS: 579357,
  
  // Logística
  PANEL_LOGISTICA: 579246,

  // Pedidos/Órdenes
  VER_PEDIDOS: 852741,
  GESTIONAR_PEDIDOS: 963852,
  ACTUALIZAR_ESTADO_PEDIDO: 321456,
  VER_DETALLES_PEDIDO: 258741,
  PANEL_VENTAS: 258147,
  VER_TRANSACCIONES: 741369,
  
  // Hero
  EDITOR_PRINCIPAL: 111222,

  // Pruebas / QA
  TESTER: 864200,

  // Reportes
  PANEL_REPORTES: 951852,
  VER_REPORTES: 159486,

  // Profesores
  VER_PROFESORES: 582147,
  CREAR_PROFESORES: 693258,
  EDITAR_PROFESORES: 714369,
  ELIMINAR_PROFESORES: 825147,
  
  // Asesorías (ids alineados con init-roles-permissions.js)
  PANEL_ASESORIAS: 369852,
  VER_ASESORIAS: 936741,
  CREAR_ASESORIAS: 147970,
  EDITAR_ASESORIAS: 258750,
  ELIMINAR_ASESORIAS: 369901,
  VER_ESTADISTICAS_ASESORIAS: 123654,
};

/**
 * Nombres de permisos comunes
 */
export const PERMISSION_NAMES = {
  PANEL_ADMIN: 'PanelAdmin',
  VER_BOTON_PANEL: 'VerBotonPanel',
  VER_PANEL_DASHBOARD: 'VerPanelDashboard',
  VER_PANEL_CURSOS: 'VerPanelCursos',
  VER_CURSOS: 'VerCursos',
  VER_CONTENIDO_CURSOS: 'VerContenidoCursos',
  EDITAR_CONTENIDO_CURSOS: 'EditarContenidoCursos',
  EDITAR_EXAMENES_CURSOS: 'EditarExamenesCursos',
  ACTIVAR_CURSOS: 'ActivarCursos',
  ARCHIVAR_CURSO: 'ArchivarCurso',
  PANEL_USERS: 'PanelUsers',
  VER_USUARIOS: 'VerUsuarios',
  EDITAR_USUARIO: 'EditarUsuario',
  ELIMINAR_USUARIO: 'EliminarUsuario',
  VER_ROLES: 'VerRoles',
  CREAR_ROLES: 'CrearRoles',
  EDITAR_ROLES: 'EditarRoles',
  ELIMINAR_ROLES: 'EliminarRoles',
  VER_ESTADISTICAS: 'VerEstadisticas',
  PANEL_EVENTOS: 'PanelEventos',
  GESTIONAR_EVENTOS: 'GestionarEventos',
  VER_EVENTOS: 'VerEventos',
  VER_EVENTOS_ARCHIVADOS: 'VerEventosArchivados',
  CREAR_EVENTOS: 'CrearEventos',
  EDITAR_EVENTOS: 'EditarEventos',
  ACTIVAR_EVENTOS: 'ActivarEventos',
  ARCHIVAR_EVENTOS: 'ArchivarEventos',
  ELIMINAR_EVENTOS: 'EliminarEventos',
  VER_BUNDLES: 'VerBundles',
  CREAR_BUNDLES: 'CrearBundles',
  EDITAR_BUNDLES: 'EditarBundles',
  ELIMINAR_BUNDLES: 'EliminarBundles',
  GESTIONAR_BUNDLES: 'GestionarBundles',
  PANEL_ITEMS: 'PanelItems',
  CREAR_ITEMS: 'CrearItems',
  VER_TODOS_ITEMS: 'VerTodosItems',
  VER_ITEMS: 'VerItems',
  EDITAR_ITEMS: 'EditarItems',
  ACTIVAR_ITEMS: 'ActivarItems',
  ARCHIVAR_ITEMS: 'ArchivarItems',
  VER_ITEMS_ARCHIVADOS: 'VerItemsArchivados',
  CREAR_CATEGORIA: 'CrearCategoria',
  EDITAR_CATEGORIA: 'EditarCategoria',
  ARCHIVAR_CATEGORIA: 'ArchivarCategoria',
  PANEL_NEWSLETTER: 'PanelNewsletter',
  GESTIONAR_NEWSLETTER: 'GestionarNewsletter',
  CREAR_NEWSLETTER: 'CrearNewsletter',
  EDITAR_NEWSLETTER: 'EditarNewsletter',
  ARCHIVAR_NEWSLETTER: 'ArchivarNewsletter',
  PUBLICAR_NEWSLETTER: 'PublicarNewsletter',
  VER_NEWSLETTER_ADMIN: 'VerNewsletterAdmin',
  VER_TODOS_NEWSLETTER_ADMIN: 'VerTodosNewsletterAdmin',
  VER_NEWSLETTERS_ARCHIVADOS: 'VerNewslettersArchivados',
  ESTADISTICAS_NEWSLETTER: 'EstadisticasNewsletter',
  PANEL_MAILING: 'PanelMailing',
  ENVIAR_MAILS: 'EnviarMails',
  VER_MAILS: 'VerMails',
  VER_DATOS_USUARIOS_MAILS: 'VerDatosUsuariosMails',
  VER_PEDIDOS: 'VerPedidos',
  GESTIONAR_PEDIDOS: 'GestionarPedidos',
  ACTUALIZAR_ESTADO_PEDIDO: 'ActualizarEstadoPedido',
  VER_DETALLES_PEDIDO: 'VerDetallesPedido',
  PANEL_VENTAS: 'PanelVentas',
  VER_TRANSACCIONES: 'VerTransacciones',
  EDITOR_PRINCIPAL: 'EditorPrincipal',
  VER_PROFESORES: 'VerProfesores',
  CREAR_PROFESORES: 'CrearProfesores',
  EDITAR_PROFESORES: 'EditarProfesores',
  ELIMINAR_PROFESORES: 'EliminarProfesores',
  PANEL_ASESORIAS: 'PanelAsesorias',
  VER_ASESORIAS: 'VerAsesorias',
  CREAR_ASESORIAS: 'CrearAsesorias',
  EDITAR_ASESORIAS: 'EditarAsesorias',
  ELIMINAR_ASESORIAS: 'EliminarAsesorias',
  VER_ESTADISTICAS_ASESORIAS: 'VerEstadisticasAsesorias',
  TESTER: 'Tester',
  PANEL_REPORTES: 'PanelReportes',
  VER_REPORTES: 'VerReportes',
};

